import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { signal } from '@preact/signals-react';
import { appendSearchParamToAnyString } from '@cortex-shim/append_search_param_to_any_string';
import { HostingService } from '@application/hosting.service';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AuthApiService } from '@api_patient/auth_api.service';
import { AsyncSignalValue } from '@component/async.component';
import { UnitService } from '@application/unit.service';

@Injectable()
export class AuthController implements ControllerProtocol {
	public methods = signal<AsyncSignalValue<Method[]>>({
		value: null,
		flowState: 'pending',
	});

	constructor(
		public readonly hostingService: HostingService,
		public readonly userSettingsService: UserSettingsService,
		public readonly universalConfig: UniversalConfig,
		private readonly authApiService: AuthApiService,
		private readonly unitService: UnitService,
	) {
		// Empty
	}

	public async whenMount() {
		const result = await this.authApiService.getMethods(this.universalConfig.licenceHolderId);
		if (result.err) {
			this.methods.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}

		this.methods.value = {
			value: result.ok.data.map((method) => ({
				scheme: method.Scheme,
				name: method.Name,
				description: method.Description,
				useIframe: method.UseIframe,
			})),
			flowState: 'accessible',
		};
	}

	public selectMethod(method: Method, returnUrl: string) {
		const params = new URLSearchParams();
		// TODO: Return url should adapt to the outgoing page
		const appendedReturnUrl = appendSearchParamToAnyString(this.getReturnUrl(returnUrl), 'openInApp', 'false');
		params.append('returnUrl', appendedReturnUrl);
		params.append('languageCode', this.userSettingsService.getSettings().selectedLanguage);

		window.location.href = `${this.hostingService.location}/api/auth/${
			this.universalConfig.licenceHolderId
		}/client/${method.scheme.toLowerCase()}/challenge?${params.toString()}`;
	}

	public getReturnUrl(url = ''): string {
		if (url === '') {
			return window.location.href;
		}

		if (url.slice(0, 4) === 'http') {
			return url;
		}

		const urlFragments = [];

		// If the url starts with a slash, we assume it's a relative path to current reception root url
		if (url.slice(0, 1) === '/') {
			const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
			urlFragments.push(this.hostingService.location, ...unit.route.split('/'));
		}
		// Otherwise, we assume it's a relative path to the current page
		else {
			// Special case to make Angular work.
			if ((window as typeof window & { __TEMP__: boolean }).__TEMP__) {
				urlFragments.push(this.hostingService.location);
			} else {
				urlFragments.push(this.hostingService.location, ...window.location.pathname.split('/'));
			}
		}

		urlFragments.push(...url.split('/'));

		// Filter out empty strings and join the fragments
		return urlFragments.filter(Boolean).join('/');
	}
}

export interface Method {
	scheme: string;
	name: string;
	description: string | null;
	useIframe: boolean;
}
